import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { DoorComponent } from "../../../models/api/doorComponents/doorComponent";
import { PredictiveService } from "../../../models/api/doorComponents/predictiveService";
import {
  PredictiveServiceHistoryObject,
  PredictiveServiceConfirmation,
  PredictiveServiceHistory,
} from "../../../models/api/doorComponents/predictiveServiceHistory";
import { Media } from "../../../models/api/media";
@Injectable({
  providedIn: "root",
})
export class DoorComponentsService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);

  /**
   * Sends a POST request to create a new door component.
   * @param doorComponent The door component to be created.
   * @returns An Observable of the response.
   */
  public createDoorComponent(doorComponent: DoorComponent) {
    return this.http
      .post(environment.baseUrl + `/gateComponent`, doorComponent)
      .pipe(this.apiErrorHandler.handle("gate_component"));
  }

  /**
   * Sends a PUT request to edit an existing door component.
   * @param doorComponent The door component to be edited.
   * @returns An Observable of the response.
   */
  public editDoorComponent(doorComponent: DoorComponent) {
    const doorComponentId = doorComponent.id;
    delete doorComponent.id;
    return this.http
      .put(environment.baseUrl + `/gateComponent/${doorComponentId}`, doorComponent)
      .pipe(this.apiErrorHandler.handle("gate_component"));
  }

  /**
   * Loads all door components from the server and converts them to predictive services.
   * @returns An Observable of the predictive services.
   */
  public getPredictiveServices() {
    return this.http.get<DoorComponent[]>(environment.baseUrl + `/gateComponent`).pipe(
      this.apiErrorHandler.handle("gate_component"),
      map((res) => {
        return this.convertToPredictiveServices(res);
      })
    );
  }

  /**
   * Sends a PUT request to edit an existing predictive service.
   * @param predictiveServiceId The id of the predictive service to be edited.
   * @param predictiveServiceEditValues The values to edit the predictive service with.
   * @returns An Observable of the predictive service confirmation.
   */
  public editPredictiveService(
    predictiveServiceId: string,
    predictiveServiceEditValues: PredictiveServiceHistoryObject
  ) {
    return this.http
      .put<PredictiveServiceConfirmation>(
        environment.baseUrl + `/predictiveService/${predictiveServiceId}`,
        predictiveServiceEditValues
      )
      .pipe(this.apiErrorHandler.handle("gate_component"));
  }

  public getPredictiveServiceAttachments(predictiveServiceId: string) {
    return this.http
      .get<Media[]>(`${environment.baseUrl}/predictiveService/${predictiveServiceId}/attachments`)
      .pipe(this.apiErrorHandler.handle("file"));
  }

  public getAuthenticatedAttachment(predictiveServiceId: string, mediaId: string): Observable<Blob | null> {
    return this.http
      .get(`${environment.baseUrl}/predictiveService/${predictiveServiceId}/attachments/${mediaId}`, {
        responseType: "blob",
      })
      .pipe(catchError(() => of(null)));
  }

  public addPredictiveServiceAttachment(predictiveServiceId: string, file: File | null, label: string) {
    const formData = new FormData();
    if (file) {
      formData.append("file", file);
    }
    formData.append("label", label);

    return this.http.post(`${environment.baseUrl}/predictiveService/${predictiveServiceId}/attachments`, formData);
  }

  public deletePredictiveServiceAttachement(predictiveServiceId: string, mediaId: string) {
    return this.http
      .delete(`${environment.baseUrl}/predictiveService/${predictiveServiceId}/attachments/${mediaId}`)
      .pipe(this.apiErrorHandler.handle("file"));
  }

  /**
   * Converts an array of DoorComponents to an array of PredictiveServices.
   * @param doorComponents The door components to convert.
   * @returns An array of PredictiveServices.
   */
  public convertToPredictiveServices(doorComponents: DoorComponent[]) {
    return doorComponents.map((doorComponent) => new PredictiveService(doorComponent));
  }

  /**
   * Deletes a door component with the given id.
   * @param doorComponentId The id of the door component to be deleted.
   * @returns An Observable of the response.
   */
  public deleteDoorComponent(doorComponentId: string | number) {
    return this.http
      .delete(environment.baseUrl + `/gateComponent/${doorComponentId}`)
      .pipe(this.apiErrorHandler.handle("gate_component"));
  }

  /**
   * Gets all door components of a door.
   * @param doorId The id of the door to get the door components for.
   * @returns An Observable of an array of door components, or null if no door components are found.
   */
  public getDoorComponents(doorId: string): Observable<DoorComponent[] | null> {
    return this.http
      .get<DoorComponent[]>(environment.baseUrl + `/gateComponent`, {
        params: {
          gateSystemId: doorId,
        },
      })
      .pipe(this.apiErrorHandler.handle("gate_component"));
  }

  /**
   * Gets the predictive service history for a given door component.
   * @param doorComponentId The id of the door component to get the predictive service history for.
   * @returns An Observable of an array of PredictiveServiceHistory objects.
   */
  public getPredictiveServiceHistory(doorComponentId: string) {
    return this.http
      .get<PredictiveServiceHistoryObject[]>(`${environment.baseUrl}/predictiveService`, {
        params: { gateComponentId: doorComponentId },
      })
      .pipe(
        this.apiErrorHandler.handle("predictive_service"),
        map((res) => {
          return res.map((predictiveService) => {
            return new PredictiveServiceHistory(predictiveService);
          });
        })
      );
  }
}
